import '../../../material/shader/helper.js';
import { LambertMaterial } from '../../../material/lambert.js';
import { BlinnMaterial } from '../../../material/blinn.js';
import { UnlitMaterial } from '../../../material/unlit.js';
import { ParticleMaterial } from '../../../material/particle.js';
import { SubsurfaceProfile } from '../../../material/subsurfaceprofile.js';
import '@zephyr3d/device';
import '../../../values.js';
import { Vector3, Vector4, Vector2 } from '@zephyr3d/base';
import { MeshMaterial } from '../../../material/meshmaterial.js';
import '../../../material/grassmaterial.js';
import '../../../material/terrain-cm.js';
import { PBRMetallicRoughnessMaterial } from '../../../material/pbrmr.js';
import { PBRSpecularGlossinessMaterial } from '../../../material/pbrsg.js';
import { MToonMaterial } from '../../../material/mtoon.js';
import { PBRBluePrintMaterial } from '../../../material/pbrblueprint.js';
import { SpriteMaterial } from '../../../material/sprite.js';
import { StandardSpriteMaterial } from '../../../material/sprite_std.js';
import { SpriteBlueprintMaterial } from '../../../material/spriteblueprint.js';
import { defineProps } from '../types.js';
import { getMeshMaterialInstanceUniformsClass, getTextureProps } from './common.js';

function createBlueprintOutputHiddenPredicate(_outputs) {
    return function() {
        return this instanceof PBRBluePrintMaterial;
    };
}
function getSubsurfaceProfileClass() {
    return {
        ctor: SubsurfaceProfile,
        name: 'SubsurfaceProfile',
        getProps () {
            return defineProps([
                {
                    name: 'Preset',
                    type: 'string',
                    default: 'skin_default',
                    options: {
                        label: 'LookPreset',
                        enum: {
                            labels: [
                                'Skin Thin',
                                'Skin Default',
                                'Skin HeavyMakeup',
                                'Wax Backlit',
                                'Wax Soft',
                                'Jade Backlit',
                                'Jade Soft'
                            ],
                            values: [
                                'skin_thin',
                                'skin_default',
                                'skin_heavy_makeup',
                                'wax_backlit',
                                'wax_soft',
                                'jade_backlit',
                                'jade_soft'
                            ]
                        }
                    },
                    get (value) {
                        value.str[0] = this.preset;
                    },
                    set (value) {
                        this.preset = value.str[0];
                    }
                },
                {
                    name: 'ScatterColor',
                    type: 'rgb',
                    default: [
                        1,
                        0.45,
                        0.17
                    ],
                    options: {
                        label: 'MeanFreePathColor',
                        animatable: true,
                        minValue: 0,
                        maxValue: 1
                    },
                    get (value) {
                        value.num[0] = this.meanFreePathColor.x;
                        value.num[1] = this.meanFreePathColor.y;
                        value.num[2] = this.meanFreePathColor.z;
                    },
                    set (value) {
                        this.meanFreePathColor = new Vector3(value.num[0], value.num[1], value.num[2]);
                    }
                },
                {
                    name: 'ScatterDistance',
                    type: 'float',
                    default: 0.92,
                    options: {
                        label: 'MeanFreePathDistance',
                        animatable: true,
                        minValue: 0,
                        maxValue: 8
                    },
                    get (value) {
                        value.num[0] = this.meanFreePathDistance;
                    },
                    set (value) {
                        this.meanFreePathDistance = value.num[0];
                    }
                },
                {
                    name: 'ScatterWeight',
                    type: 'float',
                    default: 0.82,
                    options: {
                        label: 'ScatterWeight',
                        animatable: true,
                        minValue: 0,
                        maxValue: 8
                    },
                    get (value) {
                        value.num[0] = this.strength;
                    },
                    set (value) {
                        this.strength = value.num[0];
                    }
                },
                {
                    name: 'ScatterScale',
                    type: 'float',
                    default: 0.96,
                    options: {
                        label: 'ScatterScale',
                        animatable: true,
                        minValue: 0,
                        maxValue: 8
                    },
                    get (value) {
                        value.num[0] = this.scale;
                    },
                    set (value) {
                        this.scale = value.num[0];
                    }
                },
                {
                    name: 'WorldUnitScale',
                    type: 'float',
                    default: 1,
                    options: {
                        label: 'WorldUnitScale',
                        animatable: true,
                        minValue: 0.05,
                        maxValue: 4
                    },
                    get (value) {
                        value.num[0] = this.worldUnitScale;
                    },
                    set (value) {
                        this.worldUnitScale = value.num[0];
                    }
                },
                {
                    name: 'BoundaryColorBleed',
                    type: 'float',
                    default: 0.22,
                    options: {
                        label: 'BoundaryColorBleed',
                        animatable: true,
                        minValue: 0,
                        maxValue: 1
                    },
                    get (value) {
                        value.num[0] = this.boundaryColorBleed;
                    },
                    set (value) {
                        this.boundaryColorBleed = value.num[0];
                    }
                },
                {
                    name: 'TransmissionTintColor',
                    type: 'rgb',
                    default: [
                        1,
                        0.46,
                        0.34
                    ],
                    options: {
                        label: 'TransmissionTintColor',
                        animatable: true,
                        minValue: 0,
                        maxValue: 1
                    },
                    get (value) {
                        value.num[0] = this.transmissionTintColor.x;
                        value.num[1] = this.transmissionTintColor.y;
                        value.num[2] = this.transmissionTintColor.z;
                    },
                    set (value) {
                        this.transmissionTintColor = new Vector3(value.num[0], value.num[1], value.num[2]);
                    }
                },
                {
                    name: 'ExtinctionScale',
                    type: 'float',
                    default: 1.06,
                    options: {
                        label: 'ExtinctionScale',
                        animatable: true,
                        minValue: 0,
                        maxValue: 4
                    },
                    get (value) {
                        value.num[0] = this.extinctionScale;
                    },
                    set (value) {
                        this.extinctionScale = value.num[0];
                    }
                },
                {
                    name: 'NormalScale',
                    type: 'float',
                    default: 1,
                    options: {
                        label: 'NormalScale',
                        animatable: true,
                        minValue: 0,
                        maxValue: 2
                    },
                    get (value) {
                        value.num[0] = this.normalScale;
                    },
                    set (value) {
                        this.normalScale = value.num[0];
                    }
                },
                {
                    name: 'ScatteringDistribution',
                    type: 'float',
                    default: 0.6,
                    options: {
                        label: 'ScatteringDistribution',
                        animatable: true,
                        minValue: 0,
                        maxValue: 1
                    },
                    get (value) {
                        value.num[0] = this.scatteringDistribution;
                    },
                    set (value) {
                        this.scatteringDistribution = value.num[0];
                    }
                }
            ]);
        }
    };
}
function getPBRCommonProps(manager) {
    const supportsSSSThicknessAuthoring = function() {
        return !this.$isInstance && !!(this.transmission || this.subsurfaceProfile);
    };
    return defineProps([
        {
            name: 'IOR',
            description: 'Index of refraction for the material surface',
            type: 'float',
            default: 1.5,
            options: {
                animatable: true
            },
            get (value) {
                value.num[0] = this.ior;
            },
            set (value) {
                this.ior = value.num[0];
            },
            isValid () {
                return !this.$isInstance;
            }
        },
        {
            name: 'OcclusionStrength',
            description: 'Strength multiplier for the occlusion texture effect',
            type: 'float',
            phase: 2,
            default: 1,
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 1
            },
            get (value) {
                value.num[0] = this.occlusionStrength;
            },
            set (value) {
                this.occlusionStrength = value.num[0];
            },
            isHidden: createBlueprintOutputHiddenPredicate(),
            isValid () {
                return !this.$isInstance && !!this.occlusionTexture;
            }
        },
        ...getTextureProps(manager, 'occlusionTexture', '2D', false, 0, undefined, [
            'AO'
        ]),
        {
            name: 'EmissiveColor',
            description: 'Base self-illumination color emitted by the material',
            type: 'rgb',
            options: {
                animatable: true
            },
            get (value) {
                value.num[0] = this.emissiveColor.x;
                value.num[1] = this.emissiveColor.y;
                value.num[2] = this.emissiveColor.z;
            },
            set (value) {
                this.emissiveColor = new Vector3(value.num[0], value.num[1], value.num[2]);
            },
            isHidden: createBlueprintOutputHiddenPredicate(),
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.emissiveColor : [
                    0,
                    0,
                    0
                ];
            }
        },
        {
            name: 'EmissiveStrength',
            description: 'Intensity multiplier for the emissive color and emissive texture',
            type: 'float',
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 1
            },
            get (value) {
                value.num[0] = this.emissiveStrength;
            },
            set (value) {
                this.emissiveStrength = value.num[0];
            },
            isHidden: createBlueprintOutputHiddenPredicate(),
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.emissiveStrength : 1;
            }
        },
        {
            name: 'RectSpecularScale',
            type: 'float',
            options: {
                label: 'RectSpecularScale',
                animatable: true,
                minValue: 0,
                maxValue: 4
            },
            get (value) {
                value.num[0] = this.rectSpecularScale;
            },
            set (value) {
                this.rectSpecularScale = value.num[0];
            },
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.rectSpecularScale : 1;
            }
        },
        ...getTextureProps(manager, 'emissiveTexture', '2D', true, 0, undefined, [
            'Emissive'
        ]),
        ...getTextureProps(manager, 'specularTexture', '2D', false, 0, undefined, [
            'SpecularWeight'
        ]),
        {
            name: 'Transmission',
            description: 'If true, enables light transmission through the material',
            type: 'bool',
            phase: 0,
            default: false,
            get (value) {
                value.bool[0] = this.transmission;
            },
            set (value) {
                this.transmission = value.bool[0];
            },
            isValid () {
                return !this.$isInstance;
            }
        },
        {
            name: 'TransmissionFactor',
            description: 'Amount of transmitted light, from fully opaque to fully transparent',
            type: 'float',
            phase: 1,
            default: 0,
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 1
            },
            get (value) {
                value.num[0] = this.transmissionFactor;
            },
            set (value) {
                this.transmissionFactor = value.num[0];
            },
            isValid () {
                return supportsSSSThicknessAuthoring.call(this);
            }
        },
        ...getTextureProps(manager, 'transmissionTexture', '2D', false, 1, function() {
            return this.transmission || !!this.subsurfaceProfile;
        }),
        {
            name: 'ThicknessFactor',
            description: 'Physical thickness used by transmission and volume attenuation',
            type: 'float',
            phase: 1,
            default: 0,
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 99999
            },
            get (value) {
                value.num[0] = this.thicknessFactor;
            },
            set (value) {
                this.thicknessFactor = value.num[0];
            },
            isValid () {
                return supportsSSSThicknessAuthoring.call(this);
            }
        },
        ...getTextureProps(manager, 'thicknessTexture', '2D', false, 1, function() {
            return this.transmission || !!this.subsurfaceProfile;
        }),
        {
            name: 'AttenuationColor',
            description: 'Color tint applied to transmitted light as it travels through the material',
            type: 'rgb',
            phase: 1,
            default: [
                1,
                1,
                1
            ],
            options: {
                animatable: true
            },
            get (value) {
                value.num[0] = this.attenuationColor.x;
                value.num[1] = this.attenuationColor.y;
                value.num[2] = this.attenuationColor.z;
            },
            set (value) {
                this.attenuationColor = new Vector3(value.num[0], value.num[1], value.num[2]);
            },
            isValid () {
                return supportsSSSThicknessAuthoring.call(this);
            }
        },
        {
            name: 'AttenuationDistance',
            description: 'Distance over which transmitted light is attenuated inside the material',
            type: 'float',
            phase: 1,
            default: 99999,
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 99999
            },
            get (value) {
                value.num[0] = this.attenuationDistance;
            },
            set (value) {
                this.attenuationDistance = value.num[0];
            },
            isValid () {
                return supportsSSSThicknessAuthoring.call(this);
            }
        },
        {
            name: 'Iridescence',
            description: 'If true, enables thin-film iridescence on the material surface',
            type: 'bool',
            phase: 0,
            default: false,
            get (value) {
                value.bool[0] = this.iridescence;
            },
            set (value) {
                this.iridescence = value.bool[0];
            },
            isValid () {
                return !this.$isInstance;
            }
        },
        {
            name: 'IridescenceFactor',
            description: 'Strength of the iridescence effect',
            type: 'float',
            phase: 1,
            default: 0,
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 1
            },
            get (value) {
                value.num[0] = this.iridescenceFactor;
            },
            set (value) {
                this.iridescenceFactor = value.num[0];
            },
            isValid () {
                return !this.$isInstance && !!this.iridescence;
            }
        },
        ...getTextureProps(manager, 'iridescenceTexture', '2D', false, 1, function() {
            return this.iridescence;
        }),
        {
            name: 'IridescenceIOR',
            description: 'Index of refraction used for the iridescence layer',
            type: 'float',
            phase: 1,
            default: 1.3,
            options: {
                animatable: true
            },
            get (value) {
                value.num[0] = this.iridescenceIor;
            },
            set (value) {
                this.iridescenceIor = value.num[0];
            },
            isValid () {
                return !this.$isInstance && !!this.iridescence;
            }
        },
        {
            name: 'IridescenceThicknessMin',
            description: 'Minimum thin-film thickness used for iridescence, typically in nanometers',
            type: 'float',
            phase: 1,
            default: 100,
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 1000
            },
            get (value) {
                value.num[0] = this.iridescenceThicknessMin;
            },
            set (value) {
                this.iridescenceThicknessMin = value.num[0];
            },
            isValid () {
                return !this.$isInstance && !!this.iridescence;
            }
        },
        {
            name: 'IridescenceThicknessMax',
            description: 'Maximum thin-film thickness used for iridescence, typically in nanometers',
            type: 'float',
            phase: 1,
            default: 400,
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 1000
            },
            get (value) {
                value.num[0] = this.iridescenceThicknessMax;
            },
            set (value) {
                this.iridescenceThicknessMax = value.num[0];
            },
            isValid () {
                return !this.$isInstance && !!this.iridescence;
            }
        },
        ...getTextureProps(manager, 'iridescenceThicknessTexture', '2D', false, 1, function() {
            return this.iridescence;
        }),
        {
            name: 'ClearCoat',
            description: 'If true, enables a clear coat layer on top of the material',
            type: 'bool',
            phase: 0,
            default: false,
            get (value) {
                value.bool[0] = this.clearcoat;
            },
            set (value) {
                this.clearcoat = value.bool[0];
            },
            isValid () {
                return !this.$isInstance;
            }
        },
        {
            name: 'ClearCoatIntensity',
            description: 'Strength of the clear coat layer',
            type: 'float',
            phase: 1,
            default: 0,
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 1
            },
            get (value) {
                value.num[0] = this.clearcoatIntensity;
            },
            set (value) {
                this.clearcoatIntensity = value.num[0];
            },
            isValid () {
                return !this.$isInstance && !!this.clearcoat;
            }
        },
        ...getTextureProps(manager, 'clearcoatIntensityTexture', '2D', false, 1, function() {
            return this.clearcoat;
        }),
        {
            name: 'ClearCoatRoughnessFactor',
            description: 'Roughness of the clear coat layer',
            type: 'float',
            phase: 1,
            default: 0,
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 1
            },
            get (value) {
                value.num[0] = this.clearcoatRoughnessFactor;
            },
            set (value) {
                this.clearcoatRoughnessFactor = value.num[0];
            },
            isValid () {
                return !this.$isInstance && !!this.clearcoat;
            }
        },
        ...getTextureProps(manager, 'clearcoatRoughnessTexture', '2D', false, 1, function() {
            return this.clearcoat;
        }),
        ...getTextureProps(manager, 'clearcoatNormalTexture', '2D', false, 1, function() {
            return this.clearcoat;
        }),
        {
            name: 'Sheen',
            description: 'If true, enables a soft fabric-like sheen layer',
            type: 'bool',
            phase: 0,
            default: false,
            get (value) {
                value.bool[0] = this.sheen;
            },
            set (value) {
                this.sheen = value.bool[0];
            },
            isValid () {
                return !this.$isInstance;
            }
        },
        {
            name: 'SheenColorFactor',
            description: 'Color of the sheen layer',
            type: 'rgb',
            phase: 1,
            default: [
                0,
                0,
                0
            ],
            options: {
                animatable: true
            },
            get (value) {
                value.num[0] = this.sheenColorFactor.x;
                value.num[1] = this.sheenColorFactor.y;
                value.num[2] = this.sheenColorFactor.z;
            },
            set (value) {
                this.sheenColorFactor = new Vector3(value.num[0], value.num[1], value.num[2]);
            },
            isValid () {
                return !this.$isInstance && !!this.sheen;
            }
        },
        ...getTextureProps(manager, 'sheenColorTexture', '2D', true, 1, function() {
            return this.sheen;
        }),
        {
            name: 'SheenRoughnessFactor',
            description: 'Roughness of the sheen layer',
            type: 'float',
            phase: 1,
            default: 0,
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 1
            },
            get (value) {
                value.num[0] = this.sheenRoughnessFactor;
            },
            set (value) {
                this.sheenRoughnessFactor = value.num[0];
            },
            isValid () {
                return !this.$isInstance && !!this.sheen;
            }
        },
        ...getTextureProps(manager, 'sheenRoughnessTexture', '2D', false, 1, function() {
            return this.sheen;
        }),
        ...getLitMaterialProps(manager)
    ]);
}
function getLitMaterialProps(manager) {
    return defineProps([
        ...getUnlitMaterialProps(manager),
        {
            name: 'doubleSidedLighting',
            description: 'If true, lighting is evaluated on both sides of the surface',
            type: 'bool',
            default: false,
            isValid () {
                return !this.$isInstance && this.cullMode !== 'back';
            },
            get (value) {
                value.bool[0] = this.doubleSidedLighting;
            },
            set (value) {
                this.doubleSidedLighting = value.bool[0];
            }
        },
        {
            name: 'vertexNormal',
            description: 'If true, uses vertex normals for lighting and normal mapping',
            type: 'bool',
            default: true,
            get (value) {
                value.bool[0] = this.vertexNormal;
            },
            set (value) {
                this.vertexNormal = value.bool[0];
            },
            isHidden: createBlueprintOutputHiddenPredicate(),
            isValid () {
                return !this.$isInstance;
            }
        },
        {
            name: 'vertexTangent',
            description: 'If true, uses vertex tangents for tangent-space normal mapping',
            type: 'bool',
            default: false,
            get (value) {
                value.bool[0] = this.vertexTangent;
            },
            set (value) {
                this.vertexTangent = value.bool[0];
            },
            isHidden: createBlueprintOutputHiddenPredicate(),
            isValid () {
                return !this.$isInstance && !!this.vertexNormal;
            }
        },
        ...getTextureProps(manager, 'normalTexture', '2D', false, 0, undefined, [
            'Normal'
        ])
    ]);
}
function getUnlitMaterialProps(manager) {
    return defineProps([
        {
            name: 'vertexColor',
            description: 'If true, multiplies the material color by per-vertex color data',
            type: 'bool',
            default: false,
            get (value) {
                value.bool[0] = this.vertexColor;
            },
            set (value) {
                this.vertexColor = value.bool[0];
            },
            isHidden: createBlueprintOutputHiddenPredicate(),
            isValid () {
                return !this.$isInstance;
            }
        },
        {
            name: 'AlbedoColor',
            description: 'Base RGBA color of the material before lighting',
            type: 'rgba',
            options: {
                animatable: true
            },
            get (value) {
                const color = this.albedoColor;
                value.num[0] = color.x;
                value.num[1] = color.y;
                value.num[2] = color.z;
                value.num[3] = color.w;
            },
            set (value) {
                this.albedoColor = new Vector4(value.num[0], value.num[1], value.num[2], value.num[3]);
            },
            isHidden: createBlueprintOutputHiddenPredicate(),
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.albedoColor : [
                    1,
                    1,
                    1,
                    1
                ];
            }
        },
        ...getTextureProps(manager, 'albedoTexture', '2D', true, 0, undefined, [
            'BaseColor',
            'Opacity'
        ])
    ]);
}
function getMToonMaterialProps(manager) {
    return defineProps([
        {
            name: 'AlbedoColor',
            description: 'Base RGBA color of the MToon material',
            type: 'rgba',
            options: {
                animatable: true
            },
            get (value) {
                const color = this.albedoColor;
                value.num[0] = color.x;
                value.num[1] = color.y;
                value.num[2] = color.z;
                value.num[3] = color.w;
            },
            set (value) {
                this.albedoColor = new Vector4(value.num[0], value.num[1], value.num[2], value.num[3]);
            },
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.albedoColor : [
                    1,
                    1,
                    1,
                    1
                ];
            }
        },
        ...getTextureProps(manager, 'albedoTexture', '2D', true, 0),
        {
            name: 'NormalScale',
            description: 'Scalar applied to the MToon normal texture',
            type: 'float',
            phase: 1,
            default: 1,
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 2
            },
            get (value) {
                value.num[0] = this.normalScale;
            },
            set (value) {
                this.normalScale = value.num[0];
            },
            isValid () {
                return !this.$isInstance && !!this.normalTexture;
            }
        },
        ...getTextureProps(manager, 'normalTexture', '2D', false, 0),
        {
            name: 'DoubleSidedLighting',
            description: 'If true, MToon lighting is evaluated on both sides of the surface',
            type: 'bool',
            default: true,
            get (value) {
                value.bool[0] = this.doubleSidedLighting;
            },
            set (value) {
                this.doubleSidedLighting = value.bool[0];
            }
        },
        {
            name: 'EmissiveColor',
            description: 'Base self-illumination color emitted by the MToon material',
            type: 'rgb',
            options: {
                animatable: true
            },
            get (value) {
                value.num[0] = this.emissiveColor.x;
                value.num[1] = this.emissiveColor.y;
                value.num[2] = this.emissiveColor.z;
            },
            set (value) {
                this.emissiveColor = new Vector3(value.num[0], value.num[1], value.num[2]);
            },
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.emissiveColor : [
                    0,
                    0,
                    0
                ];
            }
        },
        {
            name: 'EmissiveStrength',
            description: 'Intensity multiplier for the MToon emissive color and texture',
            type: 'float',
            default: 1,
            options: {
                animatable: true,
                minValue: 0
            },
            get (value) {
                value.num[0] = this.emissiveStrength;
            },
            set (value) {
                this.emissiveStrength = value.num[0];
            },
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.emissiveStrength : 1;
            }
        },
        ...getTextureProps(manager, 'emissiveTexture', '2D', true, 0),
        {
            name: 'ShadeColorFactor',
            description: 'MToon shadow-side color factor',
            type: 'rgb',
            default: [
                0,
                0,
                0
            ],
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 1
            },
            get (value) {
                value.num[0] = this.shadeColorFactor.x;
                value.num[1] = this.shadeColorFactor.y;
                value.num[2] = this.shadeColorFactor.z;
            },
            set (value) {
                this.shadeColorFactor = new Vector3(value.num[0], value.num[1], value.num[2]);
            },
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.shadeColorFactor : [
                    0,
                    0,
                    0
                ];
            }
        },
        ...getTextureProps(manager, 'shadeMultiplyTexture', '2D', true, 0),
        {
            name: 'ShadingShiftFactor',
            description: 'MToon terminator shift factor',
            type: 'float',
            default: 0,
            options: {
                animatable: true,
                minValue: -1,
                maxValue: 1
            },
            get (value) {
                value.num[0] = this.shadingShiftFactor;
            },
            set (value) {
                this.shadingShiftFactor = value.num[0];
            },
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.shadingShiftFactor : 0;
            }
        },
        {
            name: 'ShadingShiftTextureScale',
            description: 'Scale applied to the MToon shading shift texture',
            type: 'float',
            phase: 1,
            default: 1,
            options: {
                animatable: true
            },
            get (value) {
                value.num[0] = this.shadingShiftTextureScale;
            },
            set (value) {
                this.shadingShiftTextureScale = value.num[0];
            },
            isValid () {
                return !this.$isInstance && !!this.shadingShiftTexture;
            }
        },
        ...getTextureProps(manager, 'shadingShiftTexture', '2D', false, 0),
        {
            name: 'ShadingToonyFactor',
            description: 'MToon shadow edge sharpness factor',
            type: 'float',
            default: 0.9,
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 0.99
            },
            get (value) {
                value.num[0] = this.shadingToonyFactor;
            },
            set (value) {
                this.shadingToonyFactor = value.num[0];
            },
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.shadingToonyFactor : 0.9;
            }
        },
        {
            name: 'GIEqualizationFactor',
            description: 'MToon indirect lighting equalization factor',
            type: 'float',
            default: 0.9,
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 1
            },
            get (value) {
                value.num[0] = this.giEqualizationFactor;
            },
            set (value) {
                this.giEqualizationFactor = value.num[0];
            },
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.giEqualizationFactor : 0.9;
            }
        },
        {
            name: 'MatcapFactor',
            description: 'MToon matcap color factor',
            type: 'rgb',
            default: [
                1,
                1,
                1
            ],
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 1
            },
            get (value) {
                value.num[0] = this.matcapFactor.x;
                value.num[1] = this.matcapFactor.y;
                value.num[2] = this.matcapFactor.z;
            },
            set (value) {
                this.matcapFactor = new Vector3(value.num[0], value.num[1], value.num[2]);
            },
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.matcapFactor : [
                    1,
                    1,
                    1
                ];
            }
        },
        ...getTextureProps(manager, 'matcapTexture', '2D', true, 0),
        {
            name: 'ParametricRimColorFactor',
            description: 'MToon parametric rim color factor',
            type: 'rgb',
            default: [
                0,
                0,
                0
            ],
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 1
            },
            get (value) {
                value.num[0] = this.parametricRimColorFactor.x;
                value.num[1] = this.parametricRimColorFactor.y;
                value.num[2] = this.parametricRimColorFactor.z;
            },
            set (value) {
                this.parametricRimColorFactor = new Vector3(value.num[0], value.num[1], value.num[2]);
            },
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.parametricRimColorFactor : [
                    0,
                    0,
                    0
                ];
            }
        },
        {
            name: 'ParametricRimFresnelPowerFactor',
            description: 'MToon parametric rim Fresnel power factor',
            type: 'float',
            default: 5,
            options: {
                animatable: true,
                minValue: 0
            },
            get (value) {
                value.num[0] = this.parametricRimFresnelPowerFactor;
            },
            set (value) {
                this.parametricRimFresnelPowerFactor = value.num[0];
            },
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.parametricRimFresnelPowerFactor : 5;
            }
        },
        {
            name: 'ParametricRimLiftFactor',
            description: 'MToon parametric rim lift factor',
            type: 'float',
            default: 0,
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 1
            },
            get (value) {
                value.num[0] = this.parametricRimLiftFactor;
            },
            set (value) {
                this.parametricRimLiftFactor = value.num[0];
            },
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.parametricRimLiftFactor : 0;
            }
        },
        {
            name: 'RimLightingMixFactor',
            description: 'MToon rim lighting mix factor',
            type: 'float',
            default: 1,
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 1
            },
            get (value) {
                value.num[0] = this.rimLightingMixFactor;
            },
            set (value) {
                this.rimLightingMixFactor = value.num[0];
            },
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.rimLightingMixFactor : 1;
            }
        },
        ...getTextureProps(manager, 'rimMultiplyTexture', '2D', true, 0),
        {
            name: 'OutlineWidthMode',
            description: 'MToon outline width mode',
            type: 'string',
            default: 'none',
            options: {
                enum: {
                    labels: [
                        'None',
                        'World Coordinates',
                        'Screen Coordinates'
                    ],
                    values: [
                        'none',
                        'worldCoordinates',
                        'screenCoordinates'
                    ]
                }
            },
            get (value) {
                value.str[0] = this.outlineWidthMode;
            },
            set (value) {
                this.outlineWidthMode = value.str[0];
            },
            isValid () {
                return !this.$isInstance;
            }
        },
        {
            name: 'OutlineWidthFactor',
            description: 'MToon outline width factor',
            type: 'float',
            default: 0,
            options: {
                animatable: true,
                minValue: 0
            },
            get (value) {
                value.num[0] = this.outlineWidthFactor;
            },
            set (value) {
                this.outlineWidthFactor = value.num[0];
            },
            isValid () {
                return !this.$isInstance && this.outlineWidthMode !== 'none';
            }
        },
        ...getTextureProps(manager, 'outlineWidthMultiplyTexture', '2D', false, 1, function() {
            return this.outlineWidthMode !== 'none';
        }),
        {
            name: 'OutlineColorFactor',
            description: 'MToon outline color factor',
            type: 'rgb',
            default: [
                0,
                0,
                0
            ],
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 1
            },
            get (value) {
                value.num[0] = this.outlineColorFactor.x;
                value.num[1] = this.outlineColorFactor.y;
                value.num[2] = this.outlineColorFactor.z;
            },
            set (value) {
                this.outlineColorFactor = new Vector3(value.num[0], value.num[1], value.num[2]);
            },
            isValid () {
                return !this.$isInstance && this.outlineWidthMode !== 'none';
            }
        },
        {
            name: 'OutlineLightingMixFactor',
            description: 'MToon outline lighting mix factor',
            type: 'float',
            default: 1,
            options: {
                animatable: true,
                minValue: 0,
                maxValue: 1
            },
            get (value) {
                value.num[0] = this.outlineLightingMixFactor;
            },
            set (value) {
                this.outlineLightingMixFactor = value.num[0];
            },
            isValid () {
                return !this.$isInstance && this.outlineWidthMode !== 'none';
            }
        },
        {
            name: 'OutlineUsesTangentNormals',
            description: 'Use the tangent attribute xyz as smooth normals for MToon outline expansion',
            type: 'boolean',
            default: false,
            get (value) {
                value.bool[0] = this.outlineUsesTangentNormals;
            },
            set (value) {
                this.outlineUsesTangentNormals = value.bool[0];
            },
            isValid () {
                return !this.$isInstance && this.outlineWidthMode !== 'none';
            }
        },
        ...getTextureProps(manager, 'uvAnimationMaskTexture', '2D', false, 0),
        {
            name: 'UVAnimationScrollXSpeedFactor',
            description: 'MToon UV animation scroll speed on the X axis',
            type: 'float',
            default: 0,
            options: {
                animatable: true
            },
            get (value) {
                value.num[0] = this.uvAnimationScrollXSpeedFactor;
            },
            set (value) {
                this.uvAnimationScrollXSpeedFactor = value.num[0];
            },
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.uvAnimationScrollXSpeedFactor : 0;
            }
        },
        {
            name: 'UVAnimationScrollYSpeedFactor',
            description: 'MToon UV animation scroll speed on the Y axis',
            type: 'float',
            default: 0,
            options: {
                animatable: true
            },
            get (value) {
                value.num[0] = this.uvAnimationScrollYSpeedFactor;
            },
            set (value) {
                this.uvAnimationScrollYSpeedFactor = value.num[0];
            },
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.uvAnimationScrollYSpeedFactor : 0;
            }
        },
        {
            name: 'UVAnimationRotationSpeedFactor',
            description: 'MToon UV animation rotation speed',
            type: 'float',
            default: 0,
            options: {
                animatable: true
            },
            get (value) {
                value.num[0] = this.uvAnimationRotationSpeedFactor;
            },
            set (value) {
                this.uvAnimationRotationSpeedFactor = value.num[0];
            },
            getDefaultValue () {
                return this.$isInstance ? this.coreMaterial.uvAnimationRotationSpeedFactor : 0;
            }
        },
        {
            name: 'TransparentWithZWrite',
            description: 'If true, transparent MToon pixels also write depth',
            type: 'bool',
            default: false,
            get (value) {
                value.bool[0] = this.transparentWithZWrite;
            },
            set (value) {
                this.transparentWithZWrite = value.bool[0];
            },
            isValid () {
                return !this.$isInstance;
            }
        },
        {
            name: 'RenderQueueOffsetNumber',
            description: 'MToon render queue offset number',
            type: 'int',
            default: 0,
            options: {
                minValue: -9,
                maxValue: 9
            },
            get (value) {
                value.num[0] = this.renderQueueOffsetNumber;
            },
            set (value) {
                this.renderQueueOffsetNumber = value.num[0];
            },
            isValid () {
                return !this.$isInstance;
            }
        }
    ]);
}
/** @internal */ function getMeshMaterialClass() {
    return [
        {
            ctor: MeshMaterial,
            name: 'MeshMaterial',
            getProps () {
                return defineProps([
                    {
                        name: 'AlphaCutoff',
                        description: 'If greater then 0, pixels which have alpha smaller than alpha cutoff will be discarded',
                        type: 'float',
                        default: 0,
                        options: {
                            animatable: true,
                            minValue: 0,
                            maxValue: 1
                        },
                        get (value) {
                            value.num[0] = this.alphaCutoff;
                        },
                        set (value) {
                            this.alphaCutoff = value.num[0];
                        },
                        isValid () {
                            return !this.$isInstance;
                        }
                    },
                    {
                        name: 'AlphaToCoverage',
                        description: 'If true, alpha-to-coverage will be enabled',
                        type: 'bool',
                        default: false,
                        get (value) {
                            value.bool[0] = this.alphaToCoverage;
                        },
                        set (value) {
                            this.alphaToCoverage = value.bool[0];
                        },
                        isValid () {
                            return !this.$isInstance;
                        }
                    },
                    {
                        name: 'AlphaDither',
                        description: 'If true, alpha-dithering will be enabled',
                        type: 'bool',
                        default: false,
                        get (value) {
                            value.bool[0] = this.alphaDither;
                        },
                        set (value) {
                            this.alphaDither = value.bool[0];
                        },
                        isValid () {
                            return !this.$isInstance && this.alphaCutoff > 0;
                        }
                    },
                    {
                        name: 'BlendMode',
                        description: 'Blending mode for this material',
                        type: 'string',
                        options: {
                            enum: {
                                labels: [
                                    'None',
                                    'Blend',
                                    'Additive'
                                ],
                                values: [
                                    'none',
                                    'blend',
                                    'additive'
                                ]
                            }
                        },
                        default: 'none',
                        get (value) {
                            value.str[0] = this.blendMode;
                        },
                        set (value) {
                            this.blendMode = value.str[0];
                        },
                        isValid () {
                            return !this.$isInstance;
                        }
                    },
                    {
                        name: 'TransparentShadowCaster',
                        type: 'bool',
                        default: false,
                        get (value) {
                            value.bool[0] = this.transparentShadowCaster;
                        },
                        set (value) {
                            this.transparentShadowCaster = value.bool[0];
                        },
                        isValid () {
                            return !this.$isInstance && this.blendMode !== 'none';
                        }
                    },
                    {
                        name: 'ShadowAlphaCutoff',
                        type: 'float',
                        default: 0.5,
                        options: {
                            animatable: true,
                            minValue: 0,
                            maxValue: 1
                        },
                        get (value) {
                            value.num[0] = this.shadowAlphaCutoff;
                        },
                        set (value) {
                            this.shadowAlphaCutoff = value.num[0];
                        },
                        isValid () {
                            return !this.$isInstance && this.blendMode !== 'none' && this.transparentShadowCaster;
                        }
                    },
                    {
                        name: 'CullMode',
                        description: 'Cull mode for this material',
                        type: 'string',
                        options: {
                            enum: {
                                labels: [
                                    'None',
                                    'Front',
                                    'Back'
                                ],
                                values: [
                                    'none',
                                    'front',
                                    'back'
                                ]
                            }
                        },
                        default: 'back',
                        get (value) {
                            value.str[0] = this.cullMode;
                        },
                        set (value) {
                            this.cullMode = value.str[0];
                        },
                        isValid () {
                            return !this.$isInstance;
                        }
                    },
                    {
                        name: 'Opacity',
                        description: 'Opacity value for this material, no effect if blendingMode is `none`',
                        type: 'float',
                        options: {
                            animatable: true,
                            minValue: 0,
                            maxValue: 1
                        },
                        default: 1,
                        get (value) {
                            value.num[0] = this.opacity;
                        },
                        set (value) {
                            this.opacity = value.num[0];
                        },
                        isHidden: createBlueprintOutputHiddenPredicate(),
                        getDefaultValue () {
                            return this.$isInstance ? this.coreMaterial.opacity : 1;
                        }
                    },
                    {
                        name: 'TAAStrength',
                        description: 'TAA strength for this material',
                        type: 'float',
                        options: {
                            minValue: 0,
                            maxValue: 1
                        },
                        default: 15 / 16,
                        get (value) {
                            value.num[0] = this.TAAStrength;
                        },
                        set (value) {
                            this.TAAStrength = value.num[0];
                        },
                        isValid () {
                            return !this.$isInstance;
                        }
                    }
                ]);
            }
        },
        getMeshMaterialInstanceUniformsClass(MeshMaterial)
    ];
}
/** @internal */ function getSpriteMaterialClass(_manager) {
    return [
        {
            ctor: SpriteMaterial,
            name: 'SpriteMaterial',
            parent: MeshMaterial,
            getProps () {
                return [];
            }
        },
        getMeshMaterialInstanceUniformsClass(SpriteMaterial)
    ];
}
/** @internal */ function getStandardSpriteMaterialClass(manager) {
    return [
        {
            ctor: StandardSpriteMaterial,
            name: 'StandardSpriteMaterial',
            parent: SpriteMaterial,
            getProps () {
                return defineProps([
                    {
                        name: 'SpriteTexture',
                        description: 'Texture file path for the sprite',
                        type: 'object',
                        default: '',
                        options: {
                            mimeTypes: [
                                'image/jpeg',
                                'image/png',
                                'image/tga',
                                'image/vnd.radiance',
                                'image/x-dds',
                                'image/webp'
                            ]
                        },
                        isNullable () {
                            return true;
                        },
                        get (value) {
                            value.str[0] = manager.getAssetId(this.spriteTexture) ?? '';
                        },
                        async set (value) {
                            if (!value || !value.str[0]) {
                                this.spriteTexture = null;
                            } else {
                                const assetId = value.str[0];
                                let tex;
                                try {
                                    tex = await manager.fetchTexture(assetId, {
                                        linearColorSpace: false
                                    });
                                } catch (err) {
                                    console.error(`Load asset failed: ${value.str[0]}: ${err}`);
                                    tex = null;
                                }
                                if (tex?.isTexture2D()) {
                                    this.spriteTexture = tex;
                                } else {
                                    console.error('Invalid texture type');
                                }
                            }
                        },
                        isValid () {
                            return !this.$isInstance;
                        }
                    }
                ]);
            }
        },
        getMeshMaterialInstanceUniformsClass(StandardSpriteMaterial)
    ];
}
/** @internal */ function getParticleMaterialClass(manager) {
    return [
        {
            ctor: ParticleMaterial,
            name: 'ParticleMaterial',
            parent: MeshMaterial,
            getProps () {
                return defineProps([
                    {
                        name: 'AlphaMap',
                        description: 'Alpha texture file path',
                        type: 'object',
                        default: '',
                        options: {
                            mimeTypes: [
                                'image/jpeg',
                                'image/png',
                                'image/tga',
                                'image/vnd.radiance',
                                'image/x-dds',
                                'image/webp'
                            ]
                        },
                        isNullable () {
                            return true;
                        },
                        get (value) {
                            value.str[0] = manager.getAssetId(this.alphaMap) ?? '';
                        },
                        async set (value) {
                            if (!value || !value.str[0]) {
                                this.alphaMap = null;
                            } else {
                                const assetId = value.str[0];
                                let tex = null;
                                try {
                                    tex = await manager.fetchTexture(assetId, {
                                        linearColorSpace: true
                                    });
                                } catch (err) {
                                    console.error(`Load asset failed: ${value.str[0]}: ${err}`);
                                }
                                if (tex?.isTexture2D()) {
                                    this.alphaMap = tex;
                                } else {
                                    console.error('Invalid albedo texture');
                                }
                            }
                        }
                    },
                    {
                        name: 'RampMap',
                        description: 'Ramp texture file path',
                        type: 'object',
                        default: '',
                        options: {
                            mimeTypes: [
                                'image/jpeg',
                                'image/png',
                                'image/tga',
                                'image/vnd.radiance',
                                'image/x-dds',
                                'image/webp'
                            ]
                        },
                        isNullable () {
                            return true;
                        },
                        get (value) {
                            value.str[0] = manager.getAssetId(this.rampMap) ?? '';
                        },
                        async set (value) {
                            if (!value || !value.str[0]) {
                                this.rampMap = null;
                            } else {
                                const assetId = value.str[0];
                                let tex = null;
                                try {
                                    tex = await manager.fetchTexture(assetId);
                                } catch (err) {
                                    console.error(`Load asset failed: ${value.str[0]}: ${err}`);
                                }
                                if (tex?.isTexture2D()) {
                                    this.rampMap = tex;
                                } else {
                                    console.error('Invalid albedo texture');
                                }
                            }
                        }
                    }
                ]);
            }
        },
        getMeshMaterialInstanceUniformsClass(ParticleMaterial)
    ];
}
/** @internal */ function getPBRBluePrintMaterialClass() {
    return [
        {
            ctor: PBRBluePrintMaterial,
            parent: PBRMetallicRoughnessMaterial,
            name: 'PBRBluePrintMaterial',
            getProps () {
                return defineProps([]);
            }
        },
        getMeshMaterialInstanceUniformsClass(PBRBluePrintMaterial)
    ];
}
/** @internal */ function getSpriteBlueprintMaterialClass() {
    return [
        {
            ctor: SpriteBlueprintMaterial,
            parent: MeshMaterial,
            name: 'SpriteBlueprintMaterial',
            getProps () {
                return [];
            }
        },
        getMeshMaterialInstanceUniformsClass(SpriteBlueprintMaterial)
    ];
}
/** @internal */ function getUnlitMaterialClass(manager) {
    return [
        {
            ctor: UnlitMaterial,
            parent: MeshMaterial,
            name: 'UnlitMaterial',
            getProps () {
                return getUnlitMaterialProps(manager);
            }
        },
        getMeshMaterialInstanceUniformsClass(UnlitMaterial)
    ];
}
/** @internal */ function getMToonMaterialClass(manager) {
    return [
        {
            ctor: MToonMaterial,
            parent: MeshMaterial,
            name: 'MToonMaterial',
            getProps () {
                return getMToonMaterialProps(manager);
            }
        },
        getMeshMaterialInstanceUniformsClass(MToonMaterial)
    ];
}
/** @internal */ function getLambertMaterialClass(manager) {
    return [
        {
            ctor: LambertMaterial,
            parent: MeshMaterial,
            name: 'LambertMaterial',
            getProps () {
                return getLitMaterialProps(manager);
            }
        },
        getMeshMaterialInstanceUniformsClass(LambertMaterial)
    ];
}
/** @internal */ function getBlinnMaterialClass(manager) {
    return [
        {
            ctor: BlinnMaterial,
            parent: MeshMaterial,
            name: 'BlinnMaterial',
            getProps () {
                return defineProps([
                    {
                        name: 'Shininess',
                        description: 'Shininess value',
                        type: 'float',
                        options: {
                            animatable: true,
                            minValue: 0,
                            maxValue: 2048
                        },
                        get (value) {
                            value.num[0] = this.shininess;
                        },
                        set (value) {
                            this.shininess = value.num[0];
                        },
                        getDefaultValue () {
                            return this.$isInstance ? this.coreMaterial.shininess : 32;
                        }
                    },
                    ...getLitMaterialProps(manager)
                ]);
            }
        },
        getMeshMaterialInstanceUniformsClass(BlinnMaterial)
    ];
}
/** @internal */ function getPBRMetallicRoughnessMaterialClass(manager) {
    return [
        {
            ctor: PBRMetallicRoughnessMaterial,
            parent: MeshMaterial,
            name: 'PBRMetallicRoughnessMaterial',
            getProps () {
                return defineProps([
                    {
                        name: 'Metallic',
                        description: 'Metallic value',
                        type: 'float',
                        options: {
                            animatable: true,
                            minValue: 0,
                            maxValue: 1
                        },
                        get (value) {
                            value.num[0] = this.metallic;
                        },
                        set (value) {
                            this.metallic = value.num[0];
                        },
                        isHidden: createBlueprintOutputHiddenPredicate(),
                        getDefaultValue () {
                            return this.$isInstance ? this.coreMaterial.metallic : 1;
                        }
                    },
                    {
                        name: 'Roughness',
                        description: 'Roughness value',
                        type: 'float',
                        options: {
                            animatable: true,
                            minValue: 0,
                            maxValue: 1
                        },
                        get (value) {
                            value.num[0] = this.roughness;
                        },
                        set (value) {
                            this.roughness = value.num[0];
                        },
                        isHidden: createBlueprintOutputHiddenPredicate(),
                        getDefaultValue () {
                            return this.$isInstance ? this.coreMaterial.roughness : 1;
                        }
                    },
                    {
                        name: 'SpecularFactor',
                        description: 'RGBA specular factor used to tint and scale specular reflections',
                        type: 'rgba',
                        options: {
                            animatable: true
                        },
                        get (value) {
                            value.num[0] = this.specularFactor.x;
                            value.num[1] = this.specularFactor.y;
                            value.num[2] = this.specularFactor.z;
                            value.num[3] = this.specularFactor.w;
                        },
                        set (value) {
                            this.specularFactor = new Vector4(value.num[0], value.num[1], value.num[2], value.num[3]);
                        },
                        isHidden: createBlueprintOutputHiddenPredicate(),
                        getDefaultValue () {
                            return this.$isInstance ? this.coreMaterial.specularFactor : [
                                1,
                                1,
                                1,
                                1
                            ];
                        }
                    },
                    {
                        name: 'Reflection',
                        description: 'Reflection mode',
                        type: 'string',
                        default: 'ggx',
                        options: {
                            enum: {
                                labels: [
                                    'None',
                                    'GGX',
                                    'Anisotropic',
                                    'Glint'
                                ],
                                values: [
                                    'none',
                                    'ggx',
                                    'anisotropic',
                                    'glint'
                                ]
                            }
                        },
                        get (value) {
                            value.str[0] = this.reflectionMode;
                        },
                        set (value) {
                            this.reflectionMode = value.str[0];
                        },
                        getDefaultValue () {
                            return this.$isInstance ? this.coreMaterial.reflectionMode : 'ggx';
                        }
                    },
                    {
                        name: 'Anisotropy',
                        description: 'Strength of anisotropic reflections; values near 0 behave isotropically',
                        type: 'float',
                        default: 0.75,
                        options: {
                            animatable: true,
                            minValue: -0.95,
                            maxValue: 0.95
                        },
                        get (value) {
                            value.num[0] = this.anisotropy;
                        },
                        set (value) {
                            this.anisotropy = value.num[0];
                        },
                        getDefaultValue () {
                            return this.$isInstance ? this.coreMaterial.anisotropy : 0.75;
                        },
                        isValid () {
                            return this.reflectionMode === 'anisotropic';
                        }
                    },
                    {
                        name: 'AnisotropyDirection',
                        description: 'Rotation angle in degrees for the anisotropic highlight direction',
                        type: 'float',
                        default: 0,
                        options: {
                            animatable: true,
                            minValue: 0,
                            maxValue: 360
                        },
                        get (value) {
                            value.num[0] = this.anisotropyDirection;
                        },
                        set (value) {
                            this.anisotropyDirection = value.num[0];
                        },
                        getDefaultValue () {
                            return this.$isInstance ? this.coreMaterial.anisotropyDirection : 0;
                        },
                        isValid () {
                            return this.reflectionMode === 'anisotropic';
                        }
                    },
                    ...getTextureProps(manager, 'anisotropyDirectionTexture', '2D', false, 0, function() {
                        return this.reflectionMode === 'anisotropic';
                    }),
                    {
                        name: 'AnisotropyDirectionScaleBias',
                        description: 'Scale and bias applied when decoding anisotropy direction from its texture',
                        type: 'vec2',
                        default: [
                            1,
                            0
                        ],
                        options: {
                            animatable: true
                        },
                        get (value) {
                            value.num[0] = this.anisotropyDirectionScaleBias.x;
                            value.num[1] = this.anisotropyDirectionScaleBias.y;
                        },
                        set (value) {
                            this.anisotropyDirectionScaleBias = new Vector2(value.num[0], value.num[1]);
                        },
                        getDefaultValue () {
                            return this.$isInstance ? this.coreMaterial.anisotropyDirectionScaleBias : [
                                1,
                                0
                            ];
                        },
                        isValid () {
                            return !this.$isInstance && this.reflectionMode === 'anisotropic' && !!this.anisotropyDirectionTexture;
                        }
                    },
                    {
                        name: 'SubsurfaceProfile',
                        description: 'If true, enables subsurface scattering for translucent materials',
                        type: 'object',
                        phase: 1,
                        default: null,
                        options: {
                            objectTypes: [
                                SubsurfaceProfile
                            ]
                        },
                        isNullable () {
                            return true;
                        },
                        get (value) {
                            value.object[0] = this.subsurfaceProfile;
                        },
                        set (value) {
                            this.subsurfaceProfile = value.object[0] ?? null;
                        },
                        isValid () {
                            return !this.$isInstance;
                        }
                    },
                    ...getTextureProps(manager, 'subsurfaceTexture', '2D', false, 1, function() {
                        return !!this.subsurfaceProfile;
                    }),
                    ...getTextureProps(manager, 'metallicRoughnessTexture', '2D', false, 0, undefined, [
                        'Metallic',
                        'Roughness'
                    ]),
                    ...getTextureProps(manager, 'specularColorTexture', '2D', true, 0, undefined, [
                        'Specular'
                    ]),
                    ...getPBRCommonProps(manager)
                ]);
            }
        },
        getMeshMaterialInstanceUniformsClass(PBRMetallicRoughnessMaterial)
    ];
}
/** @internal */ function getPBRSpecularGlossinessMaterialClass(manager) {
    return [
        {
            ctor: PBRSpecularGlossinessMaterial,
            name: 'PBRSpecularGlossinessMaterial',
            parent: MeshMaterial,
            getProps () {
                return defineProps([
                    {
                        name: 'SpecularFactor',
                        description: 'RGB specular color multiplier for reflected highlights',
                        type: 'rgb',
                        options: {
                            animatable: true
                        },
                        get (value) {
                            value.num[0] = this.specularFactor.x;
                            value.num[1] = this.specularFactor.y;
                            value.num[2] = this.specularFactor.z;
                        },
                        set (value) {
                            this.specularFactor = new Vector3(value.num[0], value.num[1], value.num[2]);
                        },
                        getDefaultValue () {
                            return this.$isInstance ? this.coreMaterial.specularFactor : [
                                1,
                                1,
                                1
                            ];
                        }
                    },
                    {
                        name: 'GlossnessFactor',
                        description: 'Glossiness of the surface; higher values produce sharper reflections',
                        type: 'float',
                        options: {
                            animatable: true,
                            minValue: 0,
                            maxValue: 1
                        },
                        get (value) {
                            value.num[0] = this.glossinessFactor;
                        },
                        set (value) {
                            this.glossinessFactor = value.num[0];
                        },
                        getDefaultValue () {
                            return this.$isInstance ? this.coreMaterial.glossinessFactor : 1;
                        }
                    },
                    ...getPBRCommonProps(manager)
                ]);
            }
        },
        getMeshMaterialInstanceUniformsClass(PBRSpecularGlossinessMaterial)
    ];
}

export { getBlinnMaterialClass, getLambertMaterialClass, getMToonMaterialClass, getMeshMaterialClass, getPBRBluePrintMaterialClass, getPBRMetallicRoughnessMaterialClass, getPBRSpecularGlossinessMaterialClass, getParticleMaterialClass, getSpriteBlueprintMaterialClass, getSpriteMaterialClass, getStandardSpriteMaterialClass, getSubsurfaceProfileClass, getUnlitMaterialClass };
//# sourceMappingURL=material.js.map
